import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const OverlayContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
    align-items: flex-start;
    padding-top: 2rem;
  }
`;

const OverlayContent = styled(motion.div)`
  background: linear-gradient(135deg, #ffd93d 0%, #ff6b6b 50%, #6bcf7f 100%);
  border-radius: 20px;
  padding: 3rem;
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  margin: auto;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    max-height: calc(100vh - 4rem);
    border-radius: 15px;
    margin: 0;
  }
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  font-size: 1.5rem;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
    top: 0.8rem;
    right: 0.8rem;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const Title = styled(motion.h2)`
  text-align: center;
  font-size: 3rem;
  color: white;
  margin-bottom: 2rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
`;

const Subtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.2rem;
  color: white;
  margin-bottom: 3rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const ArtGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.2rem;
  }
`;

const ArtCard = styled(motion.a)`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 15px;
  padding: 1.5rem;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 3px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  display: block;
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    border-radius: 12px;
  }
  
  &:hover {
    border-color: #ff6b6b;
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
    text-decoration: none;
    color: inherit;
  }
`;

const ArtImage = styled(motion.div)`
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  
  @media (max-width: 768px) {
    height: 160px;
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0 5px 15px rgba(0, 0, 0, 0.2));
  }
`;

const ArtTitle = styled.h3`
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 0.8rem;
  font-weight: bold;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ArtDescription = styled.p`
  color: #666;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ArtPrice = styled.div`
  color: #ff6b6b;
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ArtButton = styled(motion.a)`
  display: inline-block;
  background: linear-gradient(45deg, #ff6b6b, #ffd93d);
  color: white;
  text-decoration: none;
  padding: 0.6rem 1.2rem;
  border-radius: 25px;
  font-weight: bold;
  font-size: 0.9rem;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    width: 100%;
    text-align: center;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4);
  }
`;

const ArtOverlay = ({ isOpen, onClose }) => {
  const artItems = [
    {
      id: 1,
      name: "Bandana Chikin Lady Canvas Art (12\" x 9\")",
      image: "/assets/products/BandanaCanvas/bandana-chikin-lady-canvas-art.jpg",
      description: "Transform your space with this stunning canvas art featuring the iconic Chikin Lady in a bandana! Perfect for adding some chicken attitude to your home decor. High-quality canvas print with vibrant colors.",
      category: "Canvas Art",
      price: "$18.99",
      buyUrl: "https://chikinlady.printify.me/product/23060057/bandana-chikin-lady-canvas-art"
    },
    {
      id: 2,
      name: "Chikin Lady Canvas Art (10\" x 8\")",
      image: "/assets/products/ChikinLadyCanvas/chikin-lady-canvas-art.jpg",
      description: "Bring the iconic Chikin Lady to your walls with this beautiful canvas art! Perfect for adding some chicken charm to your home decor. High-quality canvas print with vibrant colors and professional finish.",
      category: "Canvas Art",
      price: "$18.99",
      buyUrl: "https://chikinlady.printify.me/product/23059677/chikin-lady-canvas-art"
    },
    {
      id: 3,
      name: "Chikin Lady Acrylic Print (12\" x 12\")",
      image: "/assets/products/ChikinLadyCanvas/chikin-lady-acrylic-print.jpg",
      description: "Elevate your space with this stunning acrylic print featuring the iconic Chikin Lady! High-definition print on archival quality photographic paper fused to acrylic. Water-resistant surface with french cleat backing for easy hanging.",
      category: "Acrylic Print",
      price: "$59.99",
      buyUrl: "https://chikinlady.printify.me/product/23059069/chikin-lady-acrylic-print"
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <OverlayContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <OverlayContent
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <CloseButton
              onClick={onClose}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              ✕
            </CloseButton>
            
            <Title
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              🎨 Chikin Lady Art Gallery 🎨
            </Title>
            
            <Subtitle
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Explore our cluckin' awesome collection of original artwork!
            </Subtitle>
            
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <ArtGrid>
                {artItems.map((art) => (
                  <ArtCard
                    key={art.id}
                    href={art.buyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variants={cardVariants}
                    whileHover={{ 
                      scale: 1.02,
                      rotateY: 2
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <ArtImage
                      whileHover={{ 
                        scale: 1.05,
                        rotate: [0, -2, 2, 0]
                      }}
                      transition={{ duration: 0.5 }}
                    >
                      <img src={art.image} alt={art.name} />
                    </ArtImage>
                    
                    <ArtTitle>{art.name}</ArtTitle>
                    <ArtDescription>{art.description}</ArtDescription>
                    {art.price && <ArtPrice>{art.price}</ArtPrice>}
                    
                    <ArtButton
                      as="div"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(art.buyUrl, '_blank', 'noopener,noreferrer');
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      🛒 Buy Now
                    </ArtButton>
                  </ArtCard>
                ))}
              </ArtGrid>
            </motion.div>
          </OverlayContent>
        </OverlayContainer>
      )}
    </AnimatePresence>
  );
};

export default ArtOverlay;
